/**
 * JKL Global Wishlist Handler
 * ============================
 * Handles [data-wishlist-btn] buttons on product cards and PDP.
 *
 * Integration strategy (in priority order):
 *  1. SwymCallbacks — official Wishlist Plus callback pattern (preferred)
 *  2. window._swat  — legacy Swym global (fallback if already loaded)
 *  3. Swym REST API — direct client-side calls using cookies set by the app
 *
 * Skips buttons inside <jkl-image-with-hotspots> (managed by that component).
 *
 * Required data attributes on each button:
 *   data-product-id    – numeric Shopify product ID
 *   data-variant-id    – numeric Shopify variant ID
 *   data-product-url   – product URL path (e.g. /products/handle)
 *   data-product-title – product title (plain text)
 *   data-product-image – product image URL (may be empty)
 *   data-product-price – price as a plain number (no currency symbol)
 *
 * CSS state classes:  wishlist-btn--active | wishlist-btn--loading
 * Login detection:    window.customerLoggedIn  (set in theme.liquid)
 * Swym config:        window.swymConfig        (set in theme.liquid)
 *
 * DEBUG: localStorage.setItem('jklWishlistDebug', '1') then reload.
 */

(function () {
  'use strict';

  var LOGIN_URL = '/account/login';
  var SWYM_TIMEOUT_MS = 15000; // 15 s before giving up on Swym SDK
  var DEBUG = localStorage.getItem('jklWishlistDebug') === '1';

  var HEART_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class=""><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  var CART_SVG  = '<div class="button-bag"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24.302 27" color="#ffffff"><g transform="translate(-435.129 -151.168)"><path d="M17.766,20.25H6.574c-2.538,0-4.286-.591-5.343-1.807C.125,17.172-.231,15.253.144,12.575l1.05-8.363A5.512,5.512,0,0,1,2.8.955,3.694,3.694,0,0,1,5.31,0H19.019a3.6,3.6,0,0,1,2.466.987,5.927,5.927,0,0,1,1.661,3.225l1.039,8.363c.344,2.46-.067,4.381-1.223,5.71A6.634,6.634,0,0,1,17.766,20.25ZM16.047,4.494A1.229,1.229,0,1,0,17.24,5.723,1.212,1.212,0,0,0,16.047,4.494Zm-7.815,0A1.229,1.229,0,1,0,9.425,5.723,1.212,1.212,0,0,0,8.232,4.494Z" transform="translate(435.129 157.918)" fill="#ffffff"></path><path d="M13.465,6.445a.678.678,0,0,1-.059.3h-1.94a.876.876,0,0,1-.059-.3A4.7,4.7,0,0,0,2,6.445a.876.876,0,0,1,0,.3H.013a.876.876,0,0,1,0-.3,6.75,6.75,0,0,1,13.486,0Z" transform="translate(440.531 151.168)" fill="#ffffff"></path></g></svg></div>';

  var wishlistCount = 0; // tracked after fetch, updated on add/remove

  // ============================================
  // Logging helpers (always-on warn/error)
  // ============================================

  function log() {
    if (!DEBUG) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[jkl-wishlist]');
    console.log.apply(console, a);
  }
  function info() {
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[jkl-wishlist]');
    console.log.apply(console, a);
  }
  function warn() {
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[jkl-wishlist] WARN:');
    console.warn.apply(console, a);
  }
  function err() {
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[jkl-wishlist] ERROR:');
    console.error.apply(console, a);
  }

  // ============================================
  // Wishlist count — updates header badge
  // ============================================

  function updateWishlistCount(count) {
    wishlistCount = count;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
    log('updateWishlistCount:', count);
  }

  // ============================================
  // Swym SDK acquisition
  // ============================================

  /**
   * Returns a Promise that resolves with the swat object.
   *
   * Uses SwymCallbacks first (fires when Swym finishes its async init),
   * then falls back to polling window._swat for scripts that were already
   * loaded before ours ran.
   */
  function waitForSwym() {
    return new Promise(function (resolve, reject) {
      log('waitForSwym: start');

      // 1. Already available (Swym loaded before us)
      if (typeof window._swat !== 'undefined' && typeof window._swat.addToWishList === 'function') {
        log('waitForSwym: _swat already present');
        resolve(window._swat);
        return;
      }

      var resolved = false;
      function done(swat) {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeout);
        clearInterval(pollInterval);
        log('waitForSwym: resolved with swat', swat);
        resolve(swat);
      }

      // 2. SwymCallbacks — official pattern (Wishlist Plus v3+)
      //    The app calls each registered function with the swat object
      //    once its SDK is ready.
      log('waitForSwym: registering SwymCallbacks entry');
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(function (swat) {
        log('waitForSwym: SwymCallbacks fired');
        done(swat);
      });

      // 3. Polling fallback — catches cases where Swym loaded between the
      //    check above and the callback registration, or uses a variant of
      //    the initialisation that doesn't fire SwymCallbacks.
      var pollInterval = setInterval(function () {
        if (typeof window._swat !== 'undefined' && typeof window._swat.addToWishList === 'function') {
          log('waitForSwym: _swat appeared via poll');
          done(window._swat);
        }
      }, 200);

      // 4. Timeout — if Swym never loads (app not installed / CORS on dev)
      var timeout = setTimeout(function () {
        if (resolved) return;
        resolved = true;
        clearInterval(pollInterval);
        warn(
          'waitForSwym: Swym SDK not available after ' + (SWYM_TIMEOUT_MS / 1000) + ' s.',
          'Possible causes:',
          '  • Wishlist Plus app is not installed on this store',
          '  • CORS blocking Swym initialization on 127.0.0.1 (expected in local dev)',
          '  • App script blocked by CSP or an ad-blocker'
        );
        reject(new Error('Swym SDK not available after ' + SWYM_TIMEOUT_MS + 'ms'));
      }, SWYM_TIMEOUT_MS);
    });
  }

  // ============================================
  // Swym REST API fallback
  // ============================================

  /**
   * Reads Swym user credentials from cookies set by the Wishlist Plus app.
   * Cookie names: swym_regid, swym_sessionid, swym_lid
   * Returns null if any required cookie is missing.
   */
  function getSwymCookieCredentials() {
    function readCookie(name) {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    }
    var regid     = readCookie('swym_regid');
    var sessionid = readCookie('swym_sessionid');
    var lid       = readCookie('swym_lid') || readCookie('swym_wishlist_lid');
    log('getSwymCookieCredentials:', { regid: regid ? '***' : null, sessionid: sessionid ? '***' : null, lid: lid });
    if (!regid || !sessionid) return null;
    return { regid: regid, sessionid: sessionid, lid: lid };
  }

  /**
   * Returns the Swym REST API config from window.swymConfig (set in theme.liquid).
   * Returns null if not configured.
   */
  function getSwymRestConfig() {
    var cfg = window.swymConfig;
    if (!cfg || !cfg.pid || !cfg.endpoint) {
      log('getSwymRestConfig: window.swymConfig not set or incomplete');
      return null;
    }
    return cfg;
  }

  /**
   * Calls Swym REST API to add/remove a product.
   * Only works when:
   *  - window.swymConfig is set with pid + endpoint (theme.liquid)
   *  - The Wishlist Plus app has already set regid/sessionid cookies
   *  - The user has a wishlist list (lid cookie set)
   */
  function callSwymRestApi(action, productData) {
    var cfg   = getSwymRestConfig();
    var creds = getSwymCookieCredentials();

    if (!cfg) {
      return Promise.reject(new Error('Swym REST config not available (window.swymConfig not set)'));
    }
    if (!creds) {
      return Promise.reject(new Error('Swym cookies not found (regid/sessionid missing)'));
    }
    if (!creds.lid) {
      return Promise.reject(new Error('Swym wishlist lid not found in cookies'));
    }

    // action: 'a' = add, 'd' = delete
    var swymAction = action === 'add' ? 'a' : 'd';
    var endpoint   = cfg.endpoint.replace(/\/$/, '');
    var url        = endpoint + '/api/v3/lists/update-ctx?pid=' + encodeURIComponent(cfg.pid);

    var formData = new URLSearchParams();
    formData.append('regid',     creds.regid);
    formData.append('sessionid', creds.sessionid);
    formData.append('lid',       creds.lid);
    formData.append(swymAction,  JSON.stringify([productData]));

    log('callSwymRestApi:', action, url, productData);

    return fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    formData.toString()
    }).then(function (res) {
      if (!res.ok) throw new Error('Swym REST API error: ' + res.status);
      return res.json();
    });
  }

  // ============================================
  // Product data
  // ============================================

  function buildProductData(button) {
    var epi  = parseInt(button.dataset.variantId  || '0', 10);
    var empi = parseInt(button.dataset.productId  || '0', 10);
    var du   = button.dataset.productUrl
      ? window.location.origin + button.dataset.productUrl
      : window.location.href;
    var data = {
      epi:  epi,
      empi: empi,
      du:   du,
      dt:   button.dataset.productTitle  || '',
      iu:   button.dataset.productImage  || '',
      pr:   parseFloat(button.dataset.productPrice) || 0
    };
    log('buildProductData:', data);
    return data;
  }

  // ============================================
  // Button state helpers
  // ============================================

  function getPrefix(button) {
    return button.classList.contains('jkl-wishlist-btn') ? 'jkl-wishlist-btn' : 'wishlist-btn';
  }

  function setLoading(button, loading) {
    var prefix = getPrefix(button);
    button.classList.toggle(prefix + '--loading', loading);
    button.disabled = loading;
  }

  function setActive(button, active) {
    var prefix = getPrefix(button);
    button.classList.toggle(prefix + '--active', active);
    button.setAttribute('aria-pressed', String(active));
  }

  // ============================================
  // Init: mark buttons already in wishlist
  // ============================================

  function initWishlistStates(buttons) {
    log('initWishlistStates: customerLoggedIn =', window.customerLoggedIn);
    if (!window.customerLoggedIn) return;

    waitForSwym()
      .then(function (swat) {
        info('initWishlistStates: fetching wishlist items…');
        swat.fetch(function (items) {
          var fetchedItems = items || [];
          info('initWishlistStates: fetch returned', fetchedItems.length, 'items');
          updateWishlistCount(fetchedItems.length);
          buttons.forEach(function (btn) {
            var pid = btn.dataset.productId;
            var vid = btn.dataset.variantId;
            var found = fetchedItems.some(function (item) {
              return String(item.empi) === String(pid) || String(item.epi) === String(vid);
            });
            log('  product', pid, '/ variant', vid, '→', found ? 'IN wishlist' : 'not in wishlist');
            if (found) setActive(btn, true);
          });
        });
      })
      .catch(function (e) {
        err('initWishlistStates: Swym SDK unavailable, skipping state init.', e.message);
      });
  }

  // ============================================
  // Build wishlist empty state HTML
  // ============================================

  function buildEmptyState(container) {
    var el = container || document.querySelector('.swym-wishlist');
    var title       = (el && el.dataset.emptyTitle)       || 'La tua wishlist è vuota';
    var description = (el && el.dataset.emptyDescription) || 'Aggiungi i tuoi prodotti preferiti alla wishlist per trovarli facilmente in seguito.';
    var cta         = (el && el.dataset.emptyCta)         || 'Scopri di più';
    var ctaUrl      = (el && el.dataset.emptyCtaUrl)      || '/collections/tutti-i-prodotti';

    return (
      '<div class="jkl-wl-empty">' +
        '<div class="jkl-wl-empty__icon">' +
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">' +
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>' +
          '</svg>' +
        '</div>' +
        '<h3 class="jkl-wl-empty__title">' + title + '</h3>' +
        '<p class="jkl-wl-empty__description">' + description + '</p>' +
        '<a href="' + ctaUrl + '" class="jkl-wl-empty__cta">' + cta + '</a>' +
      '</div>'
    );
  }

  // ============================================
  // Remove card from wishlist page DOM after removal
  // ============================================

  function removeWishlistPageCard(button, wasActive) {
    if (!wasActive) return; // only remove on de-activation (remove action)
    var card = button.closest('.jkl-product-grid__item');
    if (!card) return;
    card.remove();
    var wlContainer = document.querySelector('.swym-wishlist');
    if (wlContainer && !wlContainer.querySelector('.jkl-product-grid__item')) {
      wlContainer.innerHTML = buildEmptyState(wlContainer);
    }
  }

  // ============================================
  // Toggle wishlist (add / remove)
  // ============================================

  function toggleWishlist(button) {
    var isActive     = button.classList.contains('wishlist-btn--active');
    var productData  = buildProductData(button);
    var actionName   = isActive ? 'removeFromWishList' : 'addToWishList';
    var restAction   = isActive ? 'remove' : 'add';

    info('toggleWishlist:', actionName, '— product:', productData.dt || productData.empi, '| epi:', productData.epi);
    log('toggleWishlist: full productData', productData);
    setLoading(button, true);

    // Try Swym SDK first
    waitForSwym()
      .then(function (swat) {
        swat[actionName](
          productData,
          function () {
            info(actionName, 'success — product:', productData.dt || productData.empi);
            setActive(button, !isActive);
            setLoading(button, false);
            updateWishlistCount(isActive ? wishlistCount - 1 : wishlistCount + 1);
            removeWishlistPageCard(button, isActive);
          },
          function (e) {
            err(actionName, 'failed — product:', productData.dt || productData.empi, '| error:', e);
            setLoading(button, false);
          }
        );
      })
      .catch(function (sdkErr) {
        // SDK unavailable — try REST API fallback
        warn('Swym SDK unavailable, trying REST fallback:', sdkErr.message);
        callSwymRestApi(restAction, productData)
          .then(function () {
            info('REST fallback success:', restAction, '— product:', productData.dt || productData.empi);
            setActive(button, !isActive);
            setLoading(button, false);
            updateWishlistCount(isActive ? wishlistCount - 1 : wishlistCount + 1);
            removeWishlistPageCard(button, isActive);
          })
          .catch(function (restErr) {
            err('REST fallback failed:', restErr.message);
            err(
              'Wishlist not working. Debug info:',
              '\n  window.swymConfig =', JSON.stringify(window.swymConfig),
              '\n  Swym cookies present =', !!getSwymCookieCredentials(),
              '\n  customerLoggedIn =', window.customerLoggedIn
            );
            setLoading(button, false);
          });
      });
  }

  // ============================================
  // Click handler
  // ============================================

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    var button = e.currentTarget;
    if (button.disabled) return;

    info('handleClick: "' + (button.dataset.productTitle || button.dataset.productId) + '" | loggedIn:', window.customerLoggedIn, '| active:', button.classList.contains('wishlist-btn--active'));
    log('handleClick detail:', {
      productId:    button.dataset.productId,
      variantId:    button.dataset.variantId,
      productTitle: button.dataset.productTitle,
      loggedIn:     window.customerLoggedIn
    });

    if (!window.customerLoggedIn) {
      var returnTo = window.location.pathname + window.location.search;
      window.location.href = LOGIN_URL + '?return_to=' + encodeURIComponent(returnTo);
      return;
    }

    toggleWishlist(button);
  }

  // ============================================
  // Add-to-cart for wishlist page (no routes dependency)
  // ============================================

  function handleWishlistAtc(e) {
    var btn = e.currentTarget;
    if (btn.disabled) return;
    var variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    var spinner  = btn.querySelector('.loading-overlay__spinner');
    var bagIcon  = btn.querySelector('.button-bag');
    btn.disabled = true;
    if (spinner) spinner.classList.remove('hidden');
    if (bagIcon) bagIcon.style.display = 'none';

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add ' + r.status);
        return fetch('/cart.js').then(function (r2) { return r2.json(); });
      })
      .then(function (cart) {
        // Update cart count badge everywhere
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
        // Notify cart drawer if present
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
        // Brief success state
        if (spinner) spinner.classList.add('hidden');
        var labelEl = btn.querySelector('.button-label');
        if (labelEl) { var orig = labelEl.textContent; labelEl.textContent = '✓'; setTimeout(function () { labelEl.textContent = orig; if (bagIcon) bagIcon.style.display = ''; btn.disabled = false; }, 1500); }
        else { if (bagIcon) bagIcon.style.display = ''; btn.disabled = false; }
      })
      .catch(function (e2) {
        err('handleWishlistAtc failed:', e2.message);
        if (spinner) spinner.classList.add('hidden');
        if (bagIcon) bagIcon.style.display = '';
        btn.disabled = false;
      });
  }

  // ============================================
  // Wishlist page renderer (account?view=wishlist)
  // ============================================

  function renderWishlistPageItem(item) {
    var title    = (item.dt || '').replace(/"/g, '&quot;');
    var itemUrl  = item.du || '#';
    var itemPath = itemUrl.replace(/^https?:\/\/[^/]+/, '') || '/';
    var price    = typeof item.pr === 'number' ? '€ ' + item.pr.toFixed(2).replace('.', ',') : '';
    var primaryImg = item.iu
      ? '<img class="card__img" src="' + item.iu + '" alt="' + title + '" loading="lazy" width="1000" height="1000">'
      : '';
    return (
      '<div class="jkl-product-grid__item" data-wl-epi="' + item.epi + '">' +
        '<div class="card__container" style="--aspect-ratio: 1;">' +

          // Picture area
          '<div class="card__picture-container">' +
            '<a href="' + itemUrl + '" class="card card--left" tabindex="0" aria-label="' + title + '">' +
              '<picture class="card__picture">' +
                primaryImg +
                // Hover image — src filled in by enrichWishlistCards after product.js fetch
                '<img class="card__img card__img--hover" src="" alt="' + title + '" loading="lazy" width="1000" height="1000" style="display:none">' +
              '</picture>' +
            '</a>' +
            '<button' +
              ' class="wishlist-btn wishlist-btn--active"' +
              ' type="button"' +
              ' data-wishlist-btn' +
              ' data-product-id="'    + item.empi + '"' +
              ' data-variant-id="'    + item.epi  + '"' +
              ' data-product-url="'   + itemPath.replace(/"/g, '&quot;') + '"' +
              ' data-product-title="' + title + '"' +
              ' data-product-image="' + (item.iu || '').replace(/"/g, '&quot;') + '"' +
              ' data-product-price="' + (item.pr || 0) + '"' +
              ' aria-pressed="true"' +
              ' aria-label="Rimuovi dalla wishlist"' +
            '>' + HEART_SVG + '</button>' +
          '</div>' +

          // Text content
          '<div class="card card--left">' +
            '<a href="' + itemUrl + '" class="card__overlay-link" aria-label="' + title + '"></a>' +
            // card__tags — populated by enrichWishlistCards after product.js fetch
            '<div class="card__tags"></div>' +
            '<h3 class="card__title">' +
              '<a href="' + itemUrl + '" tabindex="-1" data-omit-tabindex>' + (item.dt || '') + '</a>' +
            '</h3>' +
            // Feedaty widget — data-id/data-sku filled in by enrichWishlistCards
            '<div class="feedaty_widget" data-type="product" data-variant="Striscia-slim-blk" data-lang="all" data-gui="all" data-snippets="0"></div>' +
          '</div>' +

          // Price + Add to cart — plain button handled by handleWishlistAtc (no routes dependency)
          '<div class="card__price-add-to-cart">' +
            '<a href="' + itemUrl + '" class="card__overlay-link" aria-label="' + title + '"></a>' +
            '<div class="card__price-content-wrapper">' +
              '<div class="card__price-content">' +
                (price ? '<div class="card__price">' + price + '</div>' : '') +
              '</div>' +
              '<div class="card__quick-add-container">' +
                '<div class="wt-product__add-to-cart">' +
                  '<div class="wt-product__add-to-cart_form--wrapper">' +
                    '<button type="button" class="button--full-width hero__button--secondary" data-wl-atc' +
                      ' data-variant-id="' + item.epi + '" aria-label="Acquista">' +
                      '<span class="price">' + price + '</span>' +
                      '<span class="price">|</span>' +
                      '<span class="button-label">Acquista</span>' +
                      CART_SVG +
                      '<div class="loading-overlay__spinner hidden">' +
                        '<div class="lds-ring" aria-hidden="true"><div></div><div></div><div></div><div></div></div>' +
                      '</div>' +
                    '</button>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +

        '</div>' +
      '</div>'
    );
  }

  // Fetches product.js for each wishlist item to fill in hover image, labels, Feedaty SKU
  function enrichWishlistCards(container, items) {
    items.forEach(function (item) {
      var handle = (item.du || '').split('/products/').pop();
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var card = container.querySelector('[data-wl-epi="' + item.epi + '"]');
          if (!card) return;

          // Hover image
          if (product.images && product.images[1]) {
            var hoverImg = card.querySelector('.card__img--hover');
            if (hoverImg) {
              hoverImg.src = product.images[1];
              hoverImg.style.display = '';
            }
          }

          // Labels from product tags (matched against known label names + colors)
          var tagsEl = card.querySelector('.card__tags');
          if (tagsEl && product.tags && product.tags.length) {
            var labelMap = {
              'new':         { label: 'Novità',     color: '#ecc5de' },
              'novità':      { label: 'Novità',     color: '#ecc5de' },
              'novita':      { label: 'Novità',     color: '#ecc5de' },
              'best seller': { label: 'Best Seller', color: '#c1a8d8' },
              'bestseller':  { label: 'Best Seller', color: '#c1a8d8' },
              'promo':       { label: 'Promo',       color: '#D2416A' },
              'sale':        { label: 'Sale',        color: '#D2416A' },
              'starter pack':{ label: 'Starter Pack',color: '#D2416A' }
            };
            var tagHtml = '';
            product.tags.forEach(function (tag) {
              var match = labelMap[tag.toLowerCase()];
              if (match) {
                tagHtml += '<div class="card__tag" style="--bg-color: ' + match.color + '">' + match.label + '</div>';
              }
            });
            if (tagHtml) tagsEl.innerHTML = tagHtml;
          }

          // Feedaty widget — find variant matching epi (variant ID)
          var variant = product.variants && product.variants.find(function (v) {
            return v.id === item.epi;
          });
          var sku = (variant && variant.sku) || (product.variants && product.variants[0] && product.variants[0].sku);
          if (sku) {
            var feedatyEl = card.querySelector('.feedaty_widget');
            if (feedatyEl) {
              feedatyEl.setAttribute('data-id',  sku);
              feedatyEl.setAttribute('data-sku', sku);
              if (window.feedaty && typeof window.feedaty.init === 'function') {
                window.feedaty.init();
              }
            }
          }
        })
        .catch(function () { /* silent — enrichment is optional */ });
    });
  }

  function initWishlistPage() {
    var container = document.querySelector('.swym-wishlist');
    if (!container) return;

    info('initWishlistPage: found .swym-wishlist container');

    if (!window.customerLoggedIn) {
      container.innerHTML = '<p class="jkl-wl-empty">{{ "customer.account.wishlist.empty_title" }}</p>';
      return;
    }

    container.innerHTML = '<div class="jkl-wl-loading">…</div>';

    waitForSwym()
      .then(function (swat) {
        swat.fetch(function (items) {
          var list = items || [];
          info('initWishlistPage: rendering', list.length, 'items');
          updateWishlistCount(list.length);

          if (list.length === 0) {
            container.innerHTML = buildEmptyState(container);
            return;
          }

          container.innerHTML =
            '<div class="jkl-product-grid__products">' +
              list.map(renderWishlistPageItem).join('') +
            '</div>';

          container.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
            btn.addEventListener('click', handleClick);
          });
          container.querySelectorAll('[data-wl-atc]').forEach(function (btn) {
            btn.addEventListener('click', handleWishlistAtc);
          });
          enrichWishlistCards(container, list);
        });
      })
      .catch(function (e) {
        err('initWishlistPage: Swym SDK unavailable', e.message);
        container.innerHTML = buildEmptyState(container);
      });
  }

  // ============================================
  // Initialisation
  // ============================================

  function init() {
    var all     = Array.from(document.querySelectorAll('[data-wishlist-btn]'));
    var buttons = all;

    log('init: found', buttons.length, 'wishlist buttons');
    log('init: customerLoggedIn =', window.customerLoggedIn);
    log('init: window.swymConfig =', window.swymConfig);
    log('init: _swat present =', typeof window._swat !== 'undefined');
    log('init: SwymCallbacks registered =', (window.SwymCallbacks || []).length);

    initWishlistPage();

    buttons.forEach(function (btn) {
      btn.setAttribute('aria-pressed', 'false');
      btn.removeEventListener('click', handleClick);
      btn.addEventListener('click', handleClick);
    });

    initWishlistStates(buttons); // always runs — updates header count badge on every page
  }

  document.addEventListener('DOMContentLoaded', function () {
    log('DOMContentLoaded — init');
    init();
  });

  document.addEventListener('shopify:section:load', function () {
    log('shopify:section:load — re-init');
    init();
  });

  // Public API for manual testing from the browser console:
  //   window.__jklWishlist.waitForSwym().then(s => console.log('swat:', s))
  //   window.__jklWishlist.init()
  window.__jklWishlist = {
    init:                   init,
    waitForSwym:            waitForSwym,
    getSwymCookieCredentials: getSwymCookieCredentials,
    callSwymRestApi:        callSwymRestApi
  };
})();
